import {Card, CardBody, Row, Col} from "reactstrap";
import {BackgroundColorContext} from "../contexts/BackgroundColorContext";

const PrivacyNotice = () => {
  return (
    <BackgroundColorContext.Consumer>
      {() => (
        <>
          <Row>
            <Col md="12">
              <Card>
                <CardBody className="card-copy">
                  <h1>Privacy Notice</h1>
                  <p>
                    <strong>Last updated: October 15, 2025</strong>
                  </p>
                  <p>
                    This Privacy Notice describes how Torque Advisory LLC (&quot;we&quot;, &quot;us&quot;, or
                    &quot;our&quot;) collects, uses, and discloses your information when you use the TrainerDX service
                    (&quot;Service&quot;) and tells you about your privacy rights and how the law protects you.
                  </p>
                  <p>
                    We use your personal data to provide and improve the Service, process payments, and deliver
                    AI-powered analysis features. By using the Service, you agree to the collection and use of
                    information in accordance with this Privacy Notice.
                  </p>
                  <h1>Interpretation and Definitions</h1>
                  <h2>Interpretation</h2>
                  <p>
                    The words of which the initial letter is capitalized have meanings defined under the following
                    conditions. The following definitions shall have the same meaning regardless of whether they appear
                    in singular or in plural.
                  </p>
                  <h2>Definitions</h2>
                  <p>For the purposes of this Privacy Notice:</p>
                  <ul>
                    <li>
                      <p>
                        <strong>Account</strong> means a unique account created for You to access our Service or parts
                        of our Service.
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>Company</strong> (referred to as either &quot;the Company&quot;, &quot;We&quot;,
                        &quot;Us&quot; or &quot;Our&quot; in this Privacy Notice) refers to Torque Advisory LLC, a
                        Washington State limited liability company, operator of the TrainerDX service.
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>Cookies</strong> are small files that are placed on Your computer, mobile device or any
                        other device by a website, containing the details of Your browsing history on that website among
                        its many uses.
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>Country</strong> refers to: Washington, United States
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>Device</strong> means any device that can access the Service such as a computer, a
                        cellphone or a digital tablet.
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>Personal Data</strong> is any information that relates to an identified or identifiable
                        individual.
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>Service</strong> refers to the Website.
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>Service Provider</strong> means any natural or legal person who processes the data on
                        behalf of the Company. It refers to third-party companies or individuals employed by the Company
                        to facilitate the Service, to provide the Service on behalf of the Company, to perform services
                        related to the Service or to assist the Company in analyzing how the Service is used. This
                        includes Amazon Web Services (AWS), Stripe, and Google Analytics.
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>Usage Data</strong> refers to data collected automatically, either generated by the use
                        of the Service or from the Service infrastructure itself (for example, the duration of a page
                        visit).
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>Website</strong> refers to TrainerDXalizer, accessible from{" "}
                        <a
                          href="https://TrainerDXalizer.com"
                          rel="noreferrer external nofollow noopener"
                          target="_blank"
                        >
                          https://TrainerDXalizer.com
                        </a>
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>You</strong> means the individual accessing or using the Service, or the company, or
                        other legal entity on behalf of which such individual is accessing or using the Service, as
                        applicable.
                      </p>
                    </li>
                  </ul>
                  <h1>Collecting and Using Your Personal Data</h1>
                  <h2>Types of Data Collected</h2>
                  <h3>Personal Data</h3>
                  <p>
                    While using our Service, we may ask you to provide us with certain personally identifiable
                    information that can be used to contact or identify you. Personally identifiable information may
                    include, but is not limited to:
                  </p>
                  <ul>
                    <li>
                      <p>Email address</p>
                    </li>
                    <li>
                      <p>Name (optional, for personalized greetings)</p>
                    </li>
                    <li>
                      <p>
                        Payment information (processed by Stripe - we do not store credit card numbers or CVV codes)
                      </p>
                    </li>
                    <li>
                      <p>Timezone preference</p>
                    </li>
                    <li>
                      <p>Usage Data</p>
                    </li>
                  </ul>
                  <h3>Log File Data</h3>
                  <p>
                    When you upload ANT+ log files or other supported file formats for analysis, we collect and
                    temporarily store this data to provide our analysis services. Log files may contain:
                  </p>
                  <ul>
                    <li>
                      <p>Performance metrics (FPS, network latency, sensor data)</p>
                    </li>
                    <li>
                      <p>Hardware and software information</p>
                    </li>
                    <li>
                      <p>Activity timestamps and duration</p>
                    </li>
                    <li>
                      <p>Device identifiers and configuration</p>
                    </li>
                  </ul>
                  <p>
                    <strong>Important:</strong> Uploaded log files are automatically deleted after 7 days. Analysis
                    results generated from your log files are retained indefinitely unless you delete your account or
                    request deletion.
                  </p>
                  <h3>Payment Data</h3>
                  <p>
                    When you purchase Advanced Analysis Tokens, we collect payment-related information through our
                    third-party payment processor, Stripe. This includes:
                  </p>
                  <ul>
                    <li>
                      <p>Billing name and email</p>
                    </li>
                    <li>
                      <p>Transaction history and amounts</p>
                    </li>
                    <li>
                      <p>Token purchase records</p>
                    </li>
                  </ul>
                  <p>
                    <strong>Important:</strong> We do NOT store your credit card numbers, CVV codes, or full payment
                    card details. All payment card data is processed and stored securely by Stripe in compliance with
                    PCI-DSS standards.
                  </p>
                  <h3>Usage Data</h3>
                  <p>Usage Data is collected automatically when using the Service.</p>
                  <p>
                    Usage Data may include information such as Your Device's Internet Protocol address (e.g. IP
                    address), browser type, browser version, the pages of our Service that You visit, the time and date
                    of Your visit, the time spent on those pages, unique device identifiers and other diagnostic data.
                  </p>
                  <p>
                    When You access the Service by or through a mobile device, We may collect certain information
                    automatically, including, but not limited to, the type of mobile device You use, Your mobile device
                    unique ID, the IP address of Your mobile device, Your mobile operating system, the type of mobile
                    Internet browser You use, unique device identifiers and other diagnostic data.
                  </p>
                  <p>
                    We may also collect information that Your browser sends whenever You visit our Service or when You
                    access the Service by or through a mobile device.
                  </p>
                  <h3>Tracking Technologies and Cookies</h3>
                  <p>
                    We use Cookies and similar tracking technologies to track the activity on Our Service and store
                    certain information. Tracking technologies used are beacons, tags, and scripts to collect and track
                    information and to improve and analyze Our Service. The technologies We use may include:
                  </p>
                  <ul>
                    <li>
                      <strong>Cookies or Browser Cookies.</strong> A cookie is a small file placed on Your Device. You
                      can instruct Your browser to refuse all Cookies or to indicate when a Cookie is being sent.
                      However, if You do not accept Cookies, You may not be able to use some parts of our Service.
                      Unless you have adjusted Your browser setting so that it will refuse Cookies, our Service may use
                      Cookies.
                    </li>
                    <li>
                      <strong>Web Beacons.</strong> Certain sections of our Service and our emails may contain small
                      electronic files known as web beacons (also referred to as clear gifs, pixel tags, and
                      single-pixel gifs) that permit the Company, for example, to count users who have visited those
                      pages or opened an email and for other related website statistics (for example, recording the
                      popularity of a certain section and verifying system and server integrity).
                    </li>
                  </ul>
                  <p>
                    Cookies can be &quot;Persistent&quot; or &quot;Session&quot; Cookies. Persistent Cookies remain on
                    Your personal computer or mobile device when You go offline, while Session Cookies are deleted as
                    soon as You close Your web browser. You can learn more about cookies here:{" "}
                    <a
                      href="https://www.termsfeed.com/privacy-policy-generator/#faq-8"
                      rel="noreferrer external nofollow noopener"
                      target="_blank"
                    >
                      Cookies by TermsFeed Generator
                    </a>
                    .
                  </p>
                  <p>We use both Session and Persistent Cookies for the purposes set out below:</p>
                  <ul>
                    <li>
                      <p>
                        <strong>Necessary / Essential Cookies</strong>
                      </p>
                      <p>Type: Session Cookies</p>
                      <p>Administered by: Us</p>
                      <p>
                        Purpose: These Cookies are essential to provide You with services available through the Website
                        and to enable You to use some of its features. They help to authenticate users and prevent
                        fraudulent use of user accounts. Without these Cookies, the services that You have asked for
                        cannot be provided, and We only use these Cookies to provide You with those services.
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>Cookies Notice / Acceptance Cookies</strong>
                      </p>
                      <p>Type: Persistent Cookies</p>
                      <p>Administered by: Us</p>
                      <p>Purpose: These Cookies identify if users have accepted the use of cookies on the Website.</p>
                    </li>
                    <li>
                      <p>
                        <strong>Functionality Cookies</strong>
                      </p>
                      <p>Type: Persistent Cookies</p>
                      <p>Administered by: Us</p>
                      <p>
                        Purpose: These Cookies allow us to remember choices You make when You use the Website, such as
                        remembering your login details or language preference. The purpose of these Cookies is to
                        provide You with a more personal experience and to avoid You having to re-enter your preferences
                        every time You use the Website.
                      </p>
                    </li>
                  </ul>
                  <p>
                    For more information about the cookies we use and your choices regarding cookies, please visit our
                    Cookies Notice or the Cookies section of our Privacy Notice.
                  </p>
                  <h2>Use of Your Personal Data</h2>
                  <p>The Company may use Personal Data for the following purposes:</p>
                  <ul>
                    <li>
                      <p>
                        <strong>To provide and maintain our Service</strong>, including to monitor the usage of our
                        Service.
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>To manage Your Account:</strong> to manage Your registration as a user of the Service.
                        The Personal Data You provide can give You access to different functionalities of the Service
                        that are available to You as a registered user.
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>For the performance of a contract:</strong> the development, compliance and undertaking
                        of the purchase contract for the products, items or services You have purchased or of any other
                        contract with Us through the Service.
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>To contact You:</strong> To contact You by email, telephone calls, SMS, or other
                        equivalent forms of electronic communication, such as a mobile application's push notifications
                        regarding updates or informative communications related to the functionalities, products or
                        contracted services, including the security updates, when necessary or reasonable for their
                        implementation.
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>To manage Your requests:</strong> To attend and manage Your requests to Us.
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>For business transfers:</strong> We may use Your information to evaluate or conduct a
                        merger, divestiture, restructuring, reorganization, dissolution, or other sale or transfer of
                        some or all of Our assets, whether as a going concern or as part of bankruptcy, liquidation, or
                        similar proceeding, in which Personal Data held by Us about our Service users is among the
                        assets transferred.
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>For other purposes</strong>: We may use Your information for other purposes, such as
                        data analysis, identifying usage trends, determining the effectiveness of our promotional
                        campaigns and to evaluate and improve our Service, products, services, marketing and your
                        experience.
                      </p>
                    </li>
                  </ul>
                  <h2>Sharing of Your Personal Data</h2>
                  <p>
                    We share your personal information only with the following third-party service providers necessary
                    to operate the Service:
                  </p>
                  <ul>
                    <li>
                      <p>
                        <strong>Amazon Web Services (AWS):</strong> Cloud infrastructure for hosting, storage, and
                        computing services. AWS processes your uploaded log files and analysis results.
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>Stripe:</strong> Payment processing services. Stripe processes your payment information
                        when you purchase Advanced Analysis Tokens. Stripe maintains PCI-DSS Level 1 certification.
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>Google Analytics:</strong> Anonymous usage analytics to understand how users interact
                        with the Service. Google Analytics does NOT receive personally identifiable information or log
                        file data.
                      </p>
                    </li>
                  </ul>
                  <p>
                    We do <strong>not</strong> sell, rent, or trade your personal information to third parties for
                    marketing purposes.
                  </p>
                  <h2>Retention of Your Personal Data</h2>
                  <p>
                    The Company will retain your Personal Data only for as long as is necessary for the purposes set out
                    in this Privacy Notice. Specific retention periods include:
                  </p>
                  <ul>
                    <li>
                      <p>
                        <strong>Uploaded Log Files:</strong> Automatically deleted 7 days after upload
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>Analysis Results:</strong> Retained indefinitely unless you delete your account or
                        request deletion
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>Account Data:</strong> Retained while your account is active and for 30 days after
                        account deletion (to allow for recovery), then permanently deleted
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>Payment Records:</strong> Retained for 7 years to comply with tax and financial
                        regulations
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>Usage Data:</strong> Generally retained for internal analysis purposes for up to 2
                        years, except when used to strengthen security or improve functionality
                      </p>
                    </li>
                  </ul>
                  <p>
                    We will retain and use your Personal Data to the extent necessary to comply with our legal
                    obligations (for example, tax and accounting requirements), resolve disputes, and enforce our legal
                    agreements.
                  </p>
                  <h2>Transfer of Your Personal Data</h2>
                  <p>
                    Your information, including Personal Data, is processed at the Company's operating offices in
                    Washington State, USA, and through our cloud infrastructure providers (AWS) which may maintain data
                    centers in various locations. This means that your information may be transferred to — and
                    maintained on — computers located outside of your state, province, country or other governmental
                    jurisdiction where the data protection laws may differ from those in your jurisdiction.
                  </p>
                  <p>
                    Your consent to this Privacy Notice followed by your submission of such information represents your
                    agreement to that transfer.
                  </p>
                  <p>
                    The Company will take all steps reasonably necessary to ensure that your data is treated securely
                    and in accordance with this Privacy Notice. No transfer of your Personal Data will take place to an
                    organization or a country unless there are adequate controls in place including the security of your
                    data and other personal information.
                  </p>
                  <h2>Disclosure of Your Personal Data</h2>
                  <h3>Law enforcement</h3>
                  <p>
                    Under certain circumstances, the Company may be required to disclose your Personal Data if required
                    to do so by law or in response to valid requests by public authorities (e.g. a court or a government
                    agency).
                  </p>
                  <h3>Other legal requirements</h3>
                  <p>
                    The Company may disclose your Personal Data in the good faith belief that such action is necessary
                    to:
                  </p>
                  <ul>
                    <li>Comply with a legal obligation</li>
                    <li>Protect and defend the rights or property of Torque Advisory LLC</li>
                    <li>Prevent or investigate possible wrongdoing in connection with the Service</li>
                    <li>Protect the personal safety of Users of the Service or the public</li>
                    <li>Protect against legal liability</li>
                  </ul>
                  <h2>Security of Your Personal Data</h2>
                  <p>
                    The security of your Personal Data is important to us. We implement industry-standard security
                    measures including:
                  </p>
                  <ul>
                    <li>
                      <p>Encryption of data in transit (HTTPS/TLS) and at rest (AES-256)</p>
                    </li>
                    <li>
                      <p>AWS security best practices for cloud infrastructure</p>
                    </li>
                    <li>
                      <p>PCI-DSS compliant payment processing through Stripe</p>
                    </li>
                    <li>
                      <p>Regular security audits and monitoring</p>
                    </li>
                    <li>
                      <p>Access controls and authentication systems (AWS Cognito)</p>
                    </li>
                  </ul>
                  <p>
                    However, remember that no method of transmission over the Internet or method of electronic storage
                    is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data,
                    we cannot guarantee its absolute security.
                  </p>

                  <h2>CCPA Privacy Rights (Do Not Sell My Personal Information)</h2>

                  <p>Under the CCPA, among other rights, California consumers have the right to:</p>
                  <p>
                    Request that a business that collects a consumer's personal data disclose the categories and
                    specific pieces of personal data that a business has collected about consumers.
                  </p>
                  <p>
                    Request that a business delete any personal data about the consumer that a business has collected.
                  </p>
                  <p>
                    Request that a business that sells a consumer's personal data, not sell the consumer's personal
                    data.
                  </p>
                  <p>
                    If you make a request, we have one month to respond to you. If you would like to exercise any of
                    these rights, please contact us.
                  </p>

                  <h2>GDPR Data Protection Rights</h2>

                  <p>
                    We would like to make sure you are fully aware of all of your data protection rights. Every user is
                    entitled to the following:
                  </p>
                  <p>
                    The right to access – You have the right to request copies of your personal data. We may charge you
                    a small fee for this service.
                  </p>
                  <p>
                    The right to rectification – You have the right to request that we correct any information you
                    believe is inaccurate. You also have the right to request that we complete the information you
                    believe is incomplete.
                  </p>
                  <p>
                    The right to erasure – You have the right to request that we erase your personal data, under certain
                    conditions.
                  </p>
                  <p>
                    The right to restrict processing – You have the right to request that we restrict the processing of
                    your personal data, under certain conditions.
                  </p>
                  <p>
                    The right to object to processing – You have the right to object to our processing of your personal
                    data, under certain conditions.
                  </p>
                  <p>
                    The right to data portability – You have the right to request that we transfer the data that we have
                    collected to another organization, or directly to you, under certain conditions.
                  </p>
                  <p>
                    If you make a request, we have one month to respond to you. If you would like to exercise any of
                    these rights, please contact us.
                  </p>

                  <h1>Children's Privacy</h1>
                  <p>
                    Our Service does not address anyone under the age of 13. We do not knowingly collect personally
                    identifiable information from anyone under the age of 13. If You are a parent or guardian and You
                    are aware that Your child has provided Us with Personal Data, please contact Us. If We become aware
                    that We have collected Personal Data from anyone under the age of 13 without verification of
                    parental consent, We take steps to remove that information from Our servers.
                  </p>
                  <p>
                    If We need to rely on consent as a legal basis for processing Your information and Your country
                    requires consent from a parent, We may require Your parent's consent before We collect and use that
                    information.
                  </p>
                  <h1>Links to Other Websites</h1>
                  <p>
                    Our Service may contain links to other websites that are not operated by Us. If You click on a third
                    party link, You will be directed to that third party's site. We strongly advise You to review the
                    Privacy Notice of every site You visit.
                  </p>
                  <p>
                    We have no control over and assume no responsibility for the content, privacy notices or practices
                    of any third party sites or services.
                  </p>
                  <h1>Changes to this Privacy Notice</h1>
                  <p>
                    We may update Our Privacy Notice from time to time. We will notify You of any changes by posting the
                    new Privacy Notice on this page.
                  </p>
                  <p>
                    We will let You know via email and/or a prominent notice on Our Service, prior to the change
                    becoming effective and update the &quot;Last updated&quot; date at the top of this Privacy Notice.
                  </p>
                  <p>
                    You are advised to review this Privacy Notice periodically for any changes. Changes to this Privacy
                    Notice are effective when they are posted on this page.
                  </p>
                  <h1>Contact Us</h1>
                  <p>If you have any questions about this Privacy Notice, you can contact us:</p>
                  <ul>
                    <li>
                      <p>
                        <strong>Company:</strong> Torque Advisory LLC
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>Email:</strong> support@TrainerDXalizer.com
                      </p>
                    </li>
                    <li>
                      <p>
                        <strong>Website:</strong>{" "}
                        <a
                          href="https://www.TrainerDXalizer.com"
                          rel="noreferrer external nofollow noopener"
                          target="_blank"
                        >
                          https://www.TrainerDXalizer.com
                        </a>
                      </p>
                    </li>
                  </ul>
                  <p>
                    For data protection and privacy requests (access, deletion, correction), please email
                    support@TrainerDXalizer.com with the subject line &quot;Privacy Request&quot;.
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default PrivacyNotice;
